import { RudderAnalytics } from '@rudderstack/analytics-js/bundled';
import { getString, fetchAndActivate } from 'firebase/remote-config';
import { remoteConfig } from './firebase';

// RudderStack Analytics instance
let rudderAnalytics: RudderAnalytics | null = null;
let isInitialized = false;

/**
 * Initialize RudderStack with API key from Remote Config
 */
export const initializeAnalytics = async (): Promise<void> => {
  if (isInitialized) {
    console.log('[Analytics] Already initialized');
    return;
  }

  try {
    // Ensure Remote Config is fetched and activated
    console.log('[Analytics] Fetching Remote Config...');
    await fetchAndActivate(remoteConfig);
    console.log('[Analytics] Remote Config fetched');
    
    // Get RudderStack configuration from Remote Config
    const writeKey = getString(remoteConfig, 'rudder_stack_api_key');
    const dataPlaneUrl = getString(remoteConfig, 'rudder_stack_data_plan_url') || 'https://hosted.rudderlabs.com';

    if (!writeKey) {
      console.warn('[Analytics] RudderStack API key not found in Remote Config');
      console.warn('[Analytics] Please add "rudder_stack_api_key" to Firebase Remote Config');
      return;
    }

    console.log('[Analytics] Write key found, length:', writeKey.length);
    console.log('[Analytics] Data plane URL:', dataPlaneUrl);

    // Initialize RudderStack - using bundled version to prevent CDN plugin loading
    rudderAnalytics = new RudderAnalytics();
    await rudderAnalytics.load(writeKey, dataPlaneUrl, {
      logLevel: 'DEBUG',
      useBeacon: false,
      secureCookie: false,
      polyfillIfRequired: false,
    });

    isInitialized = true;
    console.log('[Analytics] ✅ RudderStack initialized successfully');
  } catch (error) {
    console.error('[Analytics] ❌ Failed to initialize RudderStack:', error);
  }
};

/**
 * Get the RudderStack instance
 */
const getAnalytics = (): RudderAnalytics | null => {
  if (!isInitialized || !rudderAnalytics) {
    console.warn('[Analytics] RudderStack not initialized. Call initializeAnalytics() first.');
    return null;
  }
  return rudderAnalytics;
};

// Event Names
export const ANALYTICS_EVENTS = {
  // Authentication
  USER_LOGGED_IN: 'USER_LOGGED_IN',
  ACCOUNT_CREATED: 'ACCOUNT_CREATED',
  
  // Coupon & Promo
  COUPON_SUBMITTED: 'COUPON_SUBMITTED',
  PROMO_CODE_SUBMITTED: 'PROMO_CODE_SUBMITTED',
  PROMO_CODE_REDEEMED_SUCCESS: 'PROMO_CODE_REDEEMED_SUCCESS',
  PROMO_CODE_REDEEMED_FAILED: 'PROMO_CODE_REDEEMED_FAILED',
  
  // Subscription & Billing
  PLAN_SELECTED: 'PLAN_SELECTED',
  BILLING_SUBMITTED: 'BILLING_SUBMITTED',
  BILLING_SUCCESS: 'BILLING_SUCCESS',
  BILLING_FAILED: 'BILLING_FAILED',
  SUBSCRIPTION_CANCELLED: 'SUBSCRIPTION_CANCELLED',
  SUBSCRIPTION_CANCELLATION_FAILED: 'SUBSCRIPTION_CANCELLATION_FAILED',
} as const;

// Base properties that should be included in all events
interface BaseEventProperties {
  platform: 'web';
  timestamp?: string;
}

// User properties
interface UserProperties {
  email?: string;
  userId?: string; // Firebase ID
}

// Price properties
interface PriceProperties {
  currency?: string;
  amount?: number;
  priceString?: string;
  planName?: string;
  billingInterval?: string;
}

// Combined event properties - platform is added automatically by trackEvent
type EventProperties = Partial<BaseEventProperties & UserProperties & PriceProperties> & Record<string, any>;

/**
 * Identify a user in RudderStack
 */
export const identifyUser = (userId: string, traits?: Record<string, any>): void => {
  const analytics = getAnalytics();
  if (!analytics) return;

  try {
    const userTraits = {
      platform: 'web',
      ...traits,
    };
    
    analytics.identify(userId, userTraits);
    
    console.log('[Analytics] ✅ User identified');
    console.log('[Analytics] User ID:', userId);
    console.log('[Analytics] Traits:', JSON.stringify(userTraits, null, 2));
  } catch (error) {
    console.error('[Analytics] ❌ Error identifying user:', error);
  }
};

/**
 * Track a generic event
 */
export const trackEvent = (eventName: string, properties?: EventProperties): void => {
  const analytics = getAnalytics();
  if (!analytics) return;

  try {
    const eventProperties: EventProperties = {
      platform: 'web',
      timestamp: new Date().toISOString(),
      ...properties,
    };

    analytics.track(eventName, eventProperties);
    
    console.log('[Analytics] ✅ Event tracked');
    console.log('[Analytics] Event Name:', eventName);
    console.log('[Analytics] Properties:', JSON.stringify(eventProperties, null, 2));
  } catch (error) {
    console.error('[Analytics] ❌ Error tracking event:', error);
  }
};

// ==================== Authentication Events ====================

/**
 * Track user login
 */
export const trackUserLogin = (email: string, userId: string): void => {
  trackEvent(ANALYTICS_EVENTS.USER_LOGGED_IN, {
    email,
    userId,
  });
};

/**
 * Track account creation
 */
export const trackAccountCreated = (email: string, userId: string): void => {
  trackEvent(ANALYTICS_EVENTS.ACCOUNT_CREATED, {
    email,
    userId,
  });
};

// ==================== Coupon Events ====================

/**
 * Track coupon submission
 */
export const trackCouponSubmitted = (
  couponCode: string,
  email?: string,
  userId?: string,
  isValid?: boolean
): void => {
  trackEvent(ANALYTICS_EVENTS.COUPON_SUBMITTED, {
    email,
    userId,
    couponCode,
    isValid,
  });
};

/**
 * Track promo code submission
 */
export const trackPromoCodeSubmitted = (
  promoCode: string,
  email?: string,
  userId?: string,
  isValid?: boolean,
  errorMessage?: string
): void => {
  trackEvent(ANALYTICS_EVENTS.PROMO_CODE_SUBMITTED, {
    email,
    userId,
    promoCode,
    isValid,
    errorMessage,
  });
};

/**
 * Track successful promo code redemption
 */
export const trackPromoCodeRedeemedSuccess = (
  promoCode: string,
  email?: string,
  userId?: string
): void => {
  trackEvent(ANALYTICS_EVENTS.PROMO_CODE_REDEEMED_SUCCESS, {
    email,
    userId,
    promoCode,
  });
};

/**
 * Track failed promo code redemption
 */
export const trackPromoCodeRedeemedFailed = (
  promoCode: string,
  errorMessage: string,
  email?: string,
  userId?: string
): void => {
  trackEvent(ANALYTICS_EVENTS.PROMO_CODE_REDEEMED_FAILED, {
    email,
    userId,
    promoCode,
    errorMessage,
  });
};

// ==================== Subscription & Billing Events ====================

/**
 * Track plan selection
 */
export const trackPlanSelected = (
  planName: string,
  amount: number,
  currency: string,
  billingInterval: string,
  email?: string,
  userId?: string
): void => {
  trackEvent(ANALYTICS_EVENTS.PLAN_SELECTED, {
    email,
    userId,
    planName,
    amount,
    currency,
    billingInterval,
    priceString: `${currency.toUpperCase()} ${(amount / 100).toFixed(2)}`,
  });
};

/**
 * Track billing submission
 */
export const trackBillingSubmitted = (
  planName: string,
  amount: number,
  currency: string,
  email?: string,
  userId?: string
): void => {
  trackEvent(ANALYTICS_EVENTS.BILLING_SUBMITTED, {
    email,
    userId,
    planName,
    amount,
    currency,
    priceString: `${currency.toUpperCase()} ${(amount / 100).toFixed(2)}`,
  });
};

/**
 * Track successful billing/payment
 */
export const trackBillingSuccess = (
  planName: string,
  amount: number,
  currency: string,
  email?: string,
  userId?: string,
  transactionId?: string
): void => {
  trackEvent(ANALYTICS_EVENTS.BILLING_SUCCESS, {
    email,
    userId,
    planName,
    amount,
    currency,
    priceString: `${currency.toUpperCase()} ${(amount / 100).toFixed(2)}`,
    transactionId,
  });
};

/**
 * Track failed billing/payment
 */
export const trackBillingFailed = (
  planName: string,
  amount: number,
  currency: string,
  errorMessage: string,
  email?: string,
  userId?: string
): void => {
  trackEvent(ANALYTICS_EVENTS.BILLING_FAILED, {
    email,
    userId,
    planName,
    amount,
    currency,
    priceString: `${currency.toUpperCase()} ${(amount / 100).toFixed(2)}`,
    errorMessage,
  });
};

/**
 * Track subscription cancellation
 */
export const trackSubscriptionCancelled = (
  planName: string,
  email?: string,
  userId?: string,
  cancellationReason?: string
): void => {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_CANCELLED, {
    email,
    userId,
    planName,
    cancellationReason,
  });
};

/**
 * Track failed subscription cancellation
 */
export const trackSubscriptionCancellationFailed = (
  planName: string,
  errorMessage: string,
  email?: string,
  userId?: string
): void => {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_CANCELLATION_FAILED, {
    email,
    userId,
    planName,
    errorMessage,
  });
};
